import { stripe, getStripeSecretKey } from '../config/stripe';
import { supabase, getEnvironmentAwareSupabaseUrl, getEnvironmentAwareSupabaseServiceKey } from '../config/supabase';
import { createClient } from '@supabase/supabase-js';
import Stripe from 'stripe';
// Ensure we have a server-side Stripe instance
const getStripeInstance = () => {
    if (stripe)
        return stripe;
    // Fallback: create instance if not available
    return new Stripe(getStripeSecretKey(), {
        apiVersion: '2025-10-29.clover',
        typescript: true,
    });
};
export class StripeService {
    static async createCheckoutSession(data) {
        // Client-side detection: delegate to API route when running in browser
        if (typeof window !== 'undefined') {
            try {
                const response = await fetch('/api/stripe/create-checkout-session', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(data)
                });
                if (!response.ok) {
                    const errorText = await response.text();
                    throw new Error(`API Error: ${response.status} - ${errorText}`);
                }
                return await response.json();
            }
            catch (error) {
                console.error('Client-side Stripe API call failed:', error);
                return {
                    success: false,
                    error: error instanceof Error ? error.message : 'Failed to create checkout session'
                };
            }
        }
        // Server-side: use direct Stripe SDK
        try {
            // Create service key client for RLS bypass
            const supabaseUrl = getEnvironmentAwareSupabaseUrl();
            const serviceKey = getEnvironmentAwareSupabaseServiceKey();
            console.log('🔍 StripeService: Database URL:', supabaseUrl);
            console.log('🔍 StripeService: Service key type:', typeof serviceKey);
            console.log('🔍 StripeService: Service key prefix:', serviceKey?.substring(0, 30) + '...');
            console.log('🔍 StripeService: Looking for patient ID:', data.patientId);
            console.log('🔍 StripeService: Full request data:', data);
            const serviceClient = createClient(supabaseUrl, serviceKey, {
                auth: {
                    autoRefreshToken: false,
                    persistSession: false
                }
            });
            // Get patient data - try patient ID first, then profile ID
            let patient = null;
            let patientError = null;
            // First try as patient table ID
            const { data: patientById, error: patientByIdError } = await serviceClient
                .from('patients')
                .select(`
          id, 
          profile_id,
          stripe_customer_id,
          email,
          first_name,
          last_name
        `)
                .eq('id', data.patientId)
                .maybeSingle();
            if (patientById) {
                patient = patientById;
                console.log('✅ Found patient by patient table ID');
            }
            else {
                // Try as profile ID (auth user ID)
                const { data: patientByProfile, error: patientByProfileError } = await serviceClient
                    .from('patients')
                    .select(`
            id, 
            profile_id,
            stripe_customer_id,
            email,
            first_name,
            last_name
          `)
                    .eq('profile_id', data.patientId)
                    .maybeSingle();
                if (patientByProfile) {
                    patient = patientByProfile;
                    console.log('✅ Found patient by profile ID (auth user ID)');
                }
                else {
                    patientError = patientByProfileError || patientByIdError;
                    console.log('❌ Patient not found by either patient ID or profile ID');
                }
            }
            console.log('🔍 StripeService: Patient lookup result:', { patient, patientError });
            if (patientError || !patient) {
                console.error('❌ Patient query error:', patientError);
                console.error('❌ Patient data received:', patient);
                throw new Error('Patient not found');
            }
            console.log('✅ StripeService: Patient found:', {
                id: patient.id,
                email: patient.email,
                stripe_customer_id: patient.stripe_customer_id,
                first_name: patient.first_name,
                last_name: patient.last_name
            });
            // No need to extract profile data - everything is in the patients table now!
            const stripeInstance = getStripeInstance();
            let customerId = patient.stripe_customer_id;
            console.log('🔍 StripeService: Stripe customer ID check:', customerId);
            // Create or reuse Stripe customer
            if (!customerId) {
                console.log('🔍 StripeService: Creating new Stripe customer...');
                const customer = await stripeInstance.customers.create({
                    email: patient.email,
                    name: `${patient.first_name} ${patient.last_name}`.trim(),
                    metadata: {
                        patient_id: data.patientId,
                        profile_id: patient.profile_id
                    }
                });
                customerId = customer.id;
                console.log('✅ StripeService: Stripe customer created:', customerId);
                // Save customer ID to database
                const { error: updateError } = await serviceClient
                    .from('patients')
                    .update({ stripe_customer_id: customerId })
                    .eq('id', data.patientId);
                if (updateError) {
                    console.error('❌ Failed to save Stripe customer ID:', updateError);
                }
                else {
                    console.log('✅ StripeService: Customer ID saved to database');
                }
            }
            else {
                console.log('✅ StripeService: Using existing Stripe customer ID:', customerId);
            }
            // Create checkout session with Link payment support
            console.log('🔍 StripeService: Creating checkout session with data:', {
                customer: customerId,
                priceId: data.priceId,
                successUrl: data.successUrl,
                cancelUrl: data.cancelUrl,
                patientId: data.patientId
            });
            const session = await stripeInstance.checkout.sessions.create({
                customer: customerId,
                payment_method_types: ['card', 'link'],
                line_items: [{ price: data.priceId, quantity: 1 }],
                mode: 'subscription',
                success_url: data.successUrl,
                cancel_url: data.cancelUrl,
                allow_promotion_codes: true, // Enable coupon codes in checkout
                metadata: { patient_id: data.patientId },
                subscription_data: {
                    metadata: { patient_id: data.patientId }
                },
                payment_method_options: {
                    link: {
                        persistent_token: customerId // Link payment methods to Stripe customer
                    }
                }
            });
            console.log('✅ StripeService: Checkout session created successfully:', {
                sessionId: session.id,
                url: session.url
            });
            return { success: true, sessionId: session.id, url: session.url };
        }
        catch (error) {
            console.error('❌ StripeService: Error in createCheckoutSession:', error);
            console.error('❌ StripeService: Error details:', {
                message: error instanceof Error ? error.message : 'Unknown error',
                stack: error instanceof Error ? error.stack : 'No stack trace',
                type: error instanceof Error ? error.constructor.name : typeof error,
                code: error?.code,
                statusCode: error?.statusCode
            });
            return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
        }
    }
    static async createCustomerPortalSession(customerId, returnUrl) {
        try {
            const stripeInstance = getStripeInstance();
            const session = await stripeInstance.billingPortal.sessions.create({
                customer: customerId,
                return_url: returnUrl,
            });
            return {
                success: true,
                url: session.url
            };
        }
        catch (error) {
            console.error('Error creating customer portal session:', error);
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Unknown error'
            };
        }
    }
    static async getPaymentMethods(patientId) {
        // Client-side detection: delegate to API route when running in browser
        if (typeof window !== 'undefined') {
            try {
                // Check if we're in local development
                const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
                if (isLocal) {
                    console.log('🔧 Local development detected, payment methods API not available');
                    return {
                        success: true,
                        data: []
                    };
                }
                const response = await fetch(`/api/stripe/payment-methods?patientId=${patientId}`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json'
                    }
                });
                if (!response.ok) {
                    const errorText = await response.text();
                    console.warn('🚧 Payment methods API route not available yet, falling back');
                    throw new Error('API route not ready, using fallback');
                }
                return await response.json();
            }
            catch (error) {
                console.error('Client-side payment methods API call failed:', error);
                console.warn('🔄 Payment methods API route unavailable, returning empty array until API is ready');
                return {
                    success: true,
                    data: []
                };
            }
        }
        // Server-side: use direct Stripe SDK
        try {
            // Get patient's Stripe customer ID
            const { data: patient, error: patientError } = await supabase
                .from('patients')
                .select('stripe_customer_id')
                .eq('id', patientId)
                .single();
            if (patientError || !patient) {
                throw new Error('Patient not found');
            }
            if (!patient.stripe_customer_id) {
                return {
                    success: true,
                    data: []
                };
            }
            const stripeInstance = getStripeInstance();
            // Get payment methods for customer
            const paymentMethods = await stripeInstance.paymentMethods.list({
                customer: patient.stripe_customer_id,
                type: 'card'
            });
            // Format payment method data
            const formattedMethods = paymentMethods.data.map(method => ({
                id: method.id,
                type: method.type,
                card: {
                    brand: method.card?.brand,
                    last4: method.card?.last4,
                    exp_month: method.card?.exp_month,
                    exp_year: method.card?.exp_year,
                    funding: method.card?.funding
                },
                created: new Date(method.created * 1000).toISOString(),
                is_default: false // We'll need to check if this is the default payment method
            }));
            return {
                success: true,
                data: formattedMethods
            };
        }
        catch (error) {
            console.error('Error fetching payment methods:', error);
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Failed to fetch payment methods'
            };
        }
    }
    static async getPaymentHistory(patientId) {
        // Client-side detection: delegate to API route when running in browser
        if (typeof window !== 'undefined') {
            try {
                // Check if we're in local development
                const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
                if (isLocal) {
                    console.log('🔧 Local development detected, payment history API not available');
                    return {
                        success: true,
                        data: []
                    };
                }
                const response = await fetch(`/api/stripe/payment-history?patientId=${patientId}`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json'
                    }
                });
                if (!response.ok) {
                    const errorText = await response.text();
                    console.warn('🚧 API route not available yet, falling back to direct Stripe call');
                    // Fallback: Call server-side method directly since API route isn't ready
                    throw new Error('API route not ready, using server-side fallback');
                }
                const result = await response.json();
                // Enhanced client-side debug logging for payment history
                console.log('🔍 PAYMENT HISTORY API RESPONSE:', result);
                if (result.data && Array.isArray(result.data)) {
                    console.log('📊 PAYMENT HISTORY CLIENT DEBUG:', result.data.map(payment => ({
                        id: payment.id,
                        type: payment.type,
                        amount: payment.amount,
                        currency: payment.currency,
                        description: payment.description,
                        status: payment.status,
                        created: payment.created,
                        hasInvoiceUrl: !!payment.hosted_invoice_url,
                        hasReceiptUrl: !!payment.receipt_url
                    })));
                    const paymentsByType = result.data.reduce((acc, payment) => {
                        acc[payment.type] = (acc[payment.type] || 0) + 1;
                        return acc;
                    }, {});
                    console.log('📊 PAYMENT TYPE BREAKDOWN (CLIENT):', paymentsByType);
                }
                return result;
            }
            catch (error) {
                console.error('Client-side payment history API call failed:', error);
                console.warn('🔄 API route unavailable, returning empty array until API is ready');
                // Return empty array instead of trying server-side code on client
                return {
                    success: true,
                    data: []
                };
            }
        }
        // Server-side: use direct Stripe SDK
        try {
            // Get patient's Stripe customer ID
            const { data: patient, error: patientError } = await supabase
                .from('patients')
                .select('stripe_customer_id')
                .eq('id', patientId)
                .single();
            if (patientError || !patient) {
                throw new Error('Patient not found');
            }
            if (!patient.stripe_customer_id) {
                return {
                    success: true,
                    data: []
                };
            }
            const stripeInstance = getStripeInstance();
            // Get charges (one-time payments)
            const charges = await stripeInstance.charges.list({
                customer: patient.stripe_customer_id,
                limit: 50
            });
            // Get invoices (subscription payments)
            const invoices = await stripeInstance.invoices.list({
                customer: patient.stripe_customer_id,
                limit: 50
            });
            // Combine and format payment data
            const payments = [
                // Format charges
                ...charges.data.map(charge => ({
                    id: charge.id,
                    type: 'charge',
                    amount: charge.amount / 100, // Convert from cents
                    currency: charge.currency.toUpperCase(),
                    status: charge.status,
                    description: charge.description || 'One-time payment',
                    created: new Date(charge.created * 1000).toISOString(),
                    receipt_url: charge.receipt_url
                })),
                // Format invoices
                ...invoices.data.map(invoice => ({
                    id: invoice.id,
                    type: 'invoice',
                    amount: invoice.amount_paid / 100, // Convert from cents
                    currency: invoice.currency.toUpperCase(),
                    status: invoice.status,
                    description: invoice.lines.data[0]?.description || 'Subscription payment',
                    created: new Date(invoice.created * 1000).toISOString(),
                    hosted_invoice_url: invoice.hosted_invoice_url,
                    invoice_pdf: invoice.invoice_pdf
                }))
            ];
            // Sort by date (newest first)
            payments.sort((a, b) => new Date(b.created).getTime() - new Date(a.created).getTime());
            return {
                success: true,
                data: payments
            };
        }
        catch (error) {
            console.error('Error fetching payment history:', error);
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Failed to fetch payment history'
            };
        }
    }
    static async createSetupIntent(patientId) {
        // Client-side detection: delegate to API route when running in browser
        if (typeof window !== 'undefined') {
            try {
                // Check if we're in local development
                const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
                if (isLocal) {
                    console.log('🔧 Local development detected, setup intent API not available');
                    return {
                        success: false,
                        error: 'Setup intent not available in local development'
                    };
                }
                const response = await fetch('/api/stripe/setup-intent', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({ patientId })
                });
                if (!response.ok) {
                    const errorText = await response.text();
                    console.warn('🚧 Setup intent API route not available yet, falling back');
                    throw new Error('API route not ready, using fallback');
                }
                return await response.json();
            }
            catch (error) {
                console.error('Client-side setup intent API call failed:', error);
                return {
                    success: false,
                    error: error instanceof Error ? error.message : 'Failed to create setup intent'
                };
            }
        }
        return {
            success: false,
            error: 'Setup intent only available on client-side'
        };
    }
    static async setDefaultPaymentMethod(patientId, paymentMethodId) {
        // Client-side detection: delegate to API route when running in browser
        if (typeof window !== 'undefined') {
            try {
                // Check if we're in local development
                const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
                if (isLocal) {
                    console.log('🔧 Local development detected, set default payment method API not available');
                    return {
                        success: false,
                        error: 'Set default payment method not available in local development'
                    };
                }
                const response = await fetch('/api/stripe/set-default-payment-method', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({ patientId, paymentMethodId })
                });
                if (!response.ok) {
                    const errorText = await response.text();
                    console.warn('🚧 Set default payment method API route not available yet');
                    throw new Error('API route not ready');
                }
                return await response.json();
            }
            catch (error) {
                console.error('Client-side set default payment method API call failed:', error);
                return {
                    success: false,
                    error: error instanceof Error ? error.message : 'Failed to set default payment method'
                };
            }
        }
        return {
            success: false,
            error: 'Set default payment method only available on client-side'
        };
    }
    static async deletePaymentMethod(patientId, paymentMethodId) {
        // Client-side detection: delegate to API route when running in browser
        if (typeof window !== 'undefined') {
            try {
                // Check if we're in local development
                const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
                if (isLocal) {
                    console.log('🔧 Local development detected, delete payment method API not available');
                    return {
                        success: false,
                        error: 'Delete payment method not available in local development'
                    };
                }
                const response = await fetch('/api/stripe/delete-payment-method', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({ patientId, paymentMethodId })
                });
                if (!response.ok) {
                    const errorData = await response.json();
                    console.warn('🚧 Delete payment method failed:', errorData.error);
                    throw new Error(errorData.error || 'API route not ready');
                }
                return await response.json();
            }
            catch (error) {
                console.error('Client-side delete payment method API call failed:', error);
                return {
                    success: false,
                    error: error instanceof Error ? error.message : 'Failed to delete payment method'
                };
            }
        }
        return {
            success: false,
            error: 'Delete payment method only available on client-side'
        };
    }
    static async handleWebhookEvent(eventData) {
        const event = eventData;
        // Prevent duplicate processing
        const { data: existingEvent } = await supabase
            .from('stripe_events')
            .select('id')
            .eq('stripe_event_id', event.id)
            .single();
        if (existingEvent)
            return { success: true };
        // Log event
        await supabase.from('stripe_events').insert({
            stripe_event_id: event.id,
            event_type: event.type,
            processed: false
        });
        try {
            switch (event.type) {
                case 'customer.subscription.created':
                case 'customer.subscription.updated':
                    await this.handleSubscriptionUpdate(event.data.object);
                    break;
                case 'customer.subscription.deleted':
                    await this.handleSubscriptionCancellation(event.data.object);
                    break;
                case 'checkout.session.completed':
                    await this.handleCheckoutCompleted(event.data.object);
                    break;
                case 'invoice.payment_succeeded':
                    await this.handleInvoicePaymentSucceeded(event.data.object);
                    break;
                default:
                    console.log(`Unhandled event type: ${event.type}`);
                    // Still return success for unhandled but valid webhook events
                    break;
            }
            // Mark as processed
            await supabase
                .from('stripe_events')
                .update({ processed: true })
                .eq('stripe_event_id', event.id);
            return { success: true };
        }
        catch (error) {
            console.error('Webhook error:', error);
            return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
        }
    }
    static async handleSubscriptionUpdate(subscription) {
        const customerId = subscription.customer;
        const price = subscription.items.data[0].price;
        console.log('Subscription update debug:', {
            customerId,
            subscriptionId: subscription.id,
            status: subscription.status,
            patientIdFromMetadata: subscription.metadata?.patient_id
        });
        // First check if patient exists by customer ID
        const { data: existingPatient, error: lookupError } = await supabase
            .from('patients')
            .select('id, stripe_customer_id')
            .eq('stripe_customer_id', customerId)
            .single();
        if (lookupError || !existingPatient) {
            console.error('Patient lookup failed:', { customerId, error: lookupError });
            throw new Error(`Patient not found for customer ID: ${customerId}`);
        }
        // Update patient record with subscription details
        const { error } = await supabase
            .from('patients')
            .update({
            subscription_status: subscription.status,
            stripe_customer_id: customerId,
            stripe_subscription_id: subscription.id,
            subscription_plan: price.nickname || `${price.unit_amount / 100} ${price.currency}/${price.recurring.interval}`,
            subscription_amount: price.unit_amount,
            subscription_interval: price.recurring.interval,
            current_period_end: new Date(subscription.current_period_end * 1000).toISOString(),
            trial_end: subscription.trial_end ? new Date(subscription.trial_end * 1000).toISOString() : null
        })
            .eq('stripe_customer_id', customerId);
        if (error) {
            console.error('Patient update failed:', error);
            throw error;
        }
        // Update medication preferences with next prescription due date if subscription is active
        if (subscription.status === 'active') {
            // Calculate next prescription due date based on subscription interval
            const currentDate = new Date();
            let nextDueDate = new Date(currentDate);
            if (price.recurring?.interval === 'month') {
                const intervalCount = price.recurring.interval_count || 1;
                nextDueDate.setMonth(nextDueDate.getMonth() + intervalCount);
            }
            else if (price.recurring?.interval === 'year') {
                const intervalCount = price.recurring.interval_count || 1;
                nextDueDate.setFullYear(nextDueDate.getFullYear() + intervalCount);
            }
            // Update patient medication preferences for subscription-based medications only
            // First get subscription-based medications (brand_name is null)
            const { data: subscriptionMedications } = await supabase
                .from('medications')
                .select('id')
                .or('brand_name.is.null,brand_name.eq.');
            if (subscriptionMedications && subscriptionMedications.length > 0) {
                const medicationIds = subscriptionMedications.map(med => med.id);
                const { error: prefError } = await supabase
                    .from('patient_medication_preferences')
                    .update({
                    next_prescription_due: nextDueDate.toISOString().split('T')[0] // YYYY-MM-DD format
                })
                    .eq('patient_id', existingPatient.id)
                    .in('medication_id', medicationIds);
                if (prefError) {
                    console.error('Error updating medication preferences:', prefError);
                }
                else {
                    console.log(`Updated subscription medication preferences with next due date: ${nextDueDate.toISOString().split('T')[0]}`);
                }
            }
            else {
                console.log('No subscription-based medications found - skipping preference update');
            }
        }
        console.log('Subscription update successful for patient:', existingPatient.id);
    }
    static async handleSubscriptionCancellation(subscription) {
        await supabase
            .from('patients')
            .update({
            subscription_status: 'canceled',
            current_period_end: null,
            trial_end: null
        })
            .eq('stripe_subscription_id', subscription.id);
    }
    static async handleCheckoutCompleted(session) {
        // Verify session has customer
        if (!session.customer) {
            throw new Error('Invalid checkout session: missing customer');
        }
        try {
            // Update patient with completed checkout information
            const { error } = await supabase
                .from('patients')
                .update({
                subscription_status: 'active',
                stripe_customer_id: session.customer,
                last_payment_date: new Date().toISOString()
            })
                .eq('stripe_customer_id', session.customer);
            if (error)
                throw error;
            console.log(`Checkout completed for customer: ${session.customer}`);
        }
        catch (error) {
            console.error('Error handling checkout completion:', error);
            throw error;
        }
    }
    static async handleInvoicePaymentSucceeded(invoice) {
        // Verify invoice has customer
        if (!invoice.customer) {
            throw new Error('Invalid invoice: missing customer');
        }
        try {
            // Update patient with successful payment information
            const { error: patientError } = await supabase
                .from('patients')
                .update({
                last_payment_date: new Date(invoice.created * 1000).toISOString(),
                subscription_status: 'active'
            })
                .eq('stripe_customer_id', invoice.customer);
            if (patientError)
                throw patientError;
            // Get subscription details to update medication preferences
            if (invoice.subscription) {
                const stripeInstance = getStripeInstance();
                const subscription = await stripeInstance.subscriptions.retrieve(invoice.subscription);
                const price = subscription.items.data[0]?.price;
                if (price) {
                    // Calculate next prescription due date based on subscription interval
                    const currentDate = new Date();
                    let nextDueDate = new Date(currentDate);
                    if (price.recurring?.interval === 'month') {
                        const intervalCount = price.recurring.interval_count || 1;
                        nextDueDate.setMonth(nextDueDate.getMonth() + intervalCount);
                    }
                    else if (price.recurring?.interval === 'year') {
                        const intervalCount = price.recurring.interval_count || 1;
                        nextDueDate.setFullYear(nextDueDate.getFullYear() + intervalCount);
                    }
                    // Update patient medication preferences with next due date
                    // First get the patient ID
                    const { data: patient, error: patientLookupError } = await supabase
                        .from('patients')
                        .select('id')
                        .eq('stripe_customer_id', invoice.customer)
                        .single();
                    if (!patientLookupError && patient) {
                        // Update patient medication preferences for subscription-based medications only
                        // First get subscription-based medications (brand_name is null)
                        const { data: subscriptionMedications } = await supabase
                            .from('medications')
                            .select('id')
                            .or('brand_name.is.null,brand_name.eq.');
                        if (subscriptionMedications && subscriptionMedications.length > 0) {
                            const medicationIds = subscriptionMedications.map(med => med.id);
                            const { error: prefError } = await supabase
                                .from('patient_medication_preferences')
                                .update({
                                next_prescription_due: nextDueDate.toISOString().split('T')[0] // YYYY-MM-DD format
                            })
                                .eq('patient_id', patient.id)
                                .in('medication_id', medicationIds);
                            if (prefError) {
                                console.error('Error updating medication preferences:', prefError);
                            }
                            else {
                                console.log(`Updated subscription medication preferences with next due date: ${nextDueDate.toISOString().split('T')[0]} for patient: ${patient.id}`);
                            }
                        }
                        else {
                            console.log('No subscription-based medications found - skipping preference update');
                        }
                    }
                }
            }
            console.log(`Invoice payment succeeded for customer: ${invoice.customer}, amount: ${invoice.amount_paid / 100}`);
        }
        catch (error) {
            console.error('Error handling invoice payment succeeded:', error);
            throw error;
        }
    }
    /**
     * Change subscription with automatic proration and refunds
     * Handles dosage/supply changes with immediate refunds when customer is owed money
     */
    static async changeSubscriptionWithProration(data) {
        try {
            const stripeInstance = getStripeInstance();
            console.log('🔄 Starting subscription change with proration:', {
                patientId: data.patientId,
                oldSubscriptionId: data.oldStripeSubscriptionId,
                newPriceId: data.newPriceId,
                reason: data.reason
            });
            // 1. Get current subscription details
            const currentSubscription = await stripeInstance.subscriptions.retrieve(data.oldStripeSubscriptionId);
            // Find the medication item (avoid targeting tracking or platform subscriptions)
            const medicationItem = currentSubscription.items.data.find(item => {
                const product = item.price.product;
                const productName = product?.name?.toLowerCase() || '';
                // Skip tracking/platform items, find actual medication items
                return !productName.includes('tracking') &&
                    !productName.includes('platform') &&
                    !productName.includes('service');
            });
            // If no medication item found, fall back to first item (backward compatibility)
            const targetItem = medicationItem || currentSubscription.items.data[0];
            const currentPriceId = targetItem?.price.id;
            if (!currentPriceId) {
                throw new Error('Unable to retrieve current price from subscription');
            }
            console.log('🎯 Targeting subscription item:', {
                itemId: targetItem.id,
                productName: targetItem.price.product?.name,
                currentPriceId: currentPriceId,
                isMedicationItem: !!medicationItem
            });
            // 2. Get price details for both old and new
            const [currentPrice, newPrice] = await Promise.all([
                stripeInstance.prices.retrieve(currentPriceId),
                stripeInstance.prices.retrieve(data.newPriceId)
            ]);
            const currentAmount = currentPrice.unit_amount || 0;
            const newAmount = newPrice.unit_amount || 0;
            const priceDifference = currentAmount - newAmount;
            console.log('💰 Price comparison:', {
                currentAmount: currentAmount / 100,
                newAmount: newAmount / 100,
                difference: priceDifference / 100,
                willIssueRefund: priceDifference > 0
            });
            // 3. Calculate prorated refund if customer is owed money
            let refundAmount = 0;
            if (priceDifference > 0) {
                // Calculate unused portion of current billing cycle
                const currentPeriodStart = new Date(currentSubscription.current_period_start * 1000);
                const currentPeriodEnd = new Date(currentSubscription.current_period_end * 1000);
                const now = new Date();
                const totalPeriodDays = Math.ceil((currentPeriodEnd.getTime() - currentPeriodStart.getTime()) / (1000 * 60 * 60 * 24));
                const remainingDays = Math.ceil((currentPeriodEnd.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
                // Calculate prorated refund for unused portion
                const unusedPortion = Math.max(0, remainingDays / totalPeriodDays);
                refundAmount = Math.round(priceDifference * unusedPortion);
                console.log('⏰ Proration calculation:', {
                    totalPeriodDays,
                    remainingDays,
                    unusedPortion: (unusedPortion * 100).toFixed(2) + '%',
                    refundAmount: refundAmount / 100
                });
            }
            // 4. Update subscription to new price immediately
            const updatedSubscription = await stripeInstance.subscriptions.update(data.oldStripeSubscriptionId, {
                items: [{
                        id: targetItem.id, // Use the specific item we identified, not always [0]
                        price: data.newPriceId,
                    }],
                proration_behavior: 'always_invoice', // This creates immediate proration
                metadata: {
                    ...currentSubscription.metadata,
                    change_reason: data.reason,
                    changed_at: new Date().toISOString(),
                    item_updated: targetItem.id,
                    medication_change: 'true'
                }
            });
            console.log('✅ Subscription updated successfully');
            // 5. Issue immediate refund if customer is owed money (threshold: $5+)
            let refundResult = null;
            if (refundAmount >= 500) { // $5 minimum for refunds
                try {
                    // Get the most recent successful payment for this subscription
                    const invoices = await stripeInstance.invoices.list({
                        customer: currentSubscription.customer,
                        subscription: data.oldStripeSubscriptionId,
                        status: 'paid',
                        limit: 1
                    });
                    if (invoices.data.length > 0) {
                        const latestInvoice = invoices.data[0];
                        const paymentIntent = latestInvoice.payment_intent;
                        if (paymentIntent && typeof paymentIntent === 'string') {
                            refundResult = await stripeInstance.refunds.create({
                                payment_intent: paymentIntent,
                                amount: refundAmount,
                                reason: 'requested_by_customer',
                                metadata: {
                                    patient_id: data.patientId,
                                    subscription_id: data.oldStripeSubscriptionId,
                                    change_reason: data.reason,
                                    refund_type: 'dosage_change_proration'
                                }
                            });
                            console.log('✅ Refund issued successfully:', {
                                refundId: refundResult.id,
                                amount: refundAmount / 100,
                                status: refundResult.status
                            });
                        }
                    }
                }
                catch (refundError) {
                    console.error('❌ Refund failed, falling back to customer balance credit:', refundError);
                    // Fallback: Add credit to customer balance if direct refund fails
                    await stripeInstance.customers.createBalanceTransaction(currentSubscription.customer, {
                        amount: -refundAmount, // Negative amount = credit
                        currency: currentPrice.currency,
                        description: `Credit for ${data.reason}: $${(priceDifference / 100).toFixed(2)} difference`
                    });
                }
            }
            return {
                success: true,
                subscriptionId: updatedSubscription.id,
                refundAmount: refundAmount / 100,
                refundIssued: !!refundResult,
                refundId: refundResult?.id,
                message: refundAmount >= 500
                    ? `Subscription updated and $${(refundAmount / 100).toFixed(2)} refund ${refundResult ? 'issued' : 'credited to account'}`
                    : 'Subscription updated successfully'
            };
        }
        catch (error) {
            console.error('❌ Subscription change with proration failed:', error);
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Failed to change subscription'
            };
        }
    }
    /**
     * Get customer's current balance and refund history
     */
    static async getCustomerBalance(customerId) {
        try {
            const stripeInstance = getStripeInstance();
            const [customer, balanceTransactions, refunds] = await Promise.all([
                stripeInstance.customers.retrieve(customerId),
                stripeInstance.customers.listBalanceTransactions(customerId, { limit: 10 }),
                stripeInstance.refunds.list({ customer: customerId, limit: 10 })
            ]);
            return {
                success: true,
                balance: customer.balance / 100, // Convert from cents
                balanceTransactions: balanceTransactions.data.map(txn => ({
                    id: txn.id,
                    amount: txn.amount / 100,
                    description: txn.description,
                    created: new Date(txn.created * 1000).toISOString()
                })),
                recentRefunds: refunds.data.map(refund => ({
                    id: refund.id,
                    amount: refund.amount / 100,
                    status: refund.status,
                    reason: refund.reason,
                    created: new Date(refund.created * 1000).toISOString()
                }))
            };
        }
        catch (error) {
            console.error('❌ Failed to get customer balance:', error);
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Failed to retrieve customer balance'
            };
        }
    }
    /**
     * Get customer's upcoming invoice with account credits applied
     */
    static async getUpcomingInvoice(patientId) {
        // Client-side detection: delegate to API route when running in browser
        if (typeof window !== 'undefined') {
            try {
                const response = await fetch('/api/stripe/upcoming-invoice-v2', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({ patientId })
                });
                if (!response.ok) {
                    const errorText = await response.text();
                    throw new Error(`API Error: ${response.status} - ${errorText}`);
                }
                const apiResponse = await response.json();
                // Transform API response to expected format for account dialog
                if (apiResponse.success) {
                    const nextInvoiceAmount = apiResponse.amount_due / 100; // Convert cents to dollars
                    const nextBillingDate = apiResponse.period_end ? new Date(apiResponse.period_end * 1000).toISOString() : null;
                    return {
                        success: true,
                        data: {
                            nextInvoiceAmount,
                            customerBalance: 0, // API doesn't return this separately, but amount_due already includes credits
                            nextBillingDate,
                            upcomingInvoiceTotal: apiResponse.total / 100,
                            subtotal: apiResponse.subtotal / 100,
                            total: apiResponse.total / 100,
                            amountDue: apiResponse.amount_due / 100,
                            currency: apiResponse.currency
                        }
                    };
                }
                return apiResponse;
            }
            catch (error) {
                console.error('Client-side upcoming invoice API call failed:', error);
                return {
                    success: false,
                    error: error instanceof Error ? error.message : 'Failed to get upcoming invoice'
                };
            }
        }
        // Server-side implementation
        try {
            const stripeInstance = getStripeInstance();
            // Get patient's Stripe customer ID
            const supabaseClient = createClient(getEnvironmentAwareSupabaseUrl(), getEnvironmentAwareSupabaseServiceKey());
            const { data: patient, error: patientError } = await supabaseClient
                .from('patients')
                .select('stripe_customer_id')
                .eq('id', patientId)
                .single();
            if (patientError || !patient?.stripe_customer_id) {
                throw new Error('Patient not found or missing Stripe customer ID');
            }
            // Get upcoming invoice and customer balance
            const [upcomingInvoice, customer] = await Promise.all([
                stripeInstance.invoices.retrieveUpcoming({
                    customer: patient.stripe_customer_id
                }).catch(() => null), // Handle case where no upcoming invoice exists
                stripeInstance.customers.retrieve(patient.stripe_customer_id)
            ]);
            const customerBalance = customer.balance / 100; // Convert from cents
            let nextInvoiceAmount = 0;
            let nextBillingDate = null;
            if (upcomingInvoice) {
                nextInvoiceAmount = (upcomingInvoice.amount_due - upcomingInvoice.amount_paid) / 100;
                nextBillingDate = new Date(upcomingInvoice.created * 1000).toISOString();
                // If customer has credit balance, apply it to the invoice amount
                if (customerBalance > 0) {
                    nextInvoiceAmount = Math.max(0, nextInvoiceAmount - customerBalance);
                }
            }
            return {
                success: true,
                data: {
                    nextInvoiceAmount,
                    nextBillingDate,
                    customerBalance,
                    hasUpcomingInvoice: !!upcomingInvoice,
                    upcomingInvoiceTotal: upcomingInvoice ? upcomingInvoice.total / 100 : 0
                }
            };
        }
        catch (error) {
            console.error('❌ Failed to get upcoming invoice:', error);
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Failed to retrieve upcoming invoice'
            };
        }
    }
    /**
     * Client-side method to trigger medication change with automatic proration and refunds
     */
    static async changeMedicationWithProration(data) {
        // Client-side detection: delegate to API route when running in browser
        if (typeof window !== 'undefined') {
            try {
                const response = await fetch('/api/medication/change-subscription', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        ...data,
                        changedBy: data.changedBy || 'patient'
                    })
                });
                if (!response.ok) {
                    const errorText = await response.text();
                    throw new Error(`API Error: ${response.status} - ${errorText}`);
                }
                return await response.json();
            }
            catch (error) {
                console.error('Client-side medication change API call failed:', error);
                return {
                    success: false,
                    error: error instanceof Error ? error.message : 'Failed to change medication subscription'
                };
            }
        }
        // Server-side: delegate to API endpoint for medication changes
        // Note: MedicationChangeHandler temporarily excluded from build due to TypeScript issues
        console.log('📋 Medication change functionality available via API endpoint');
        return {
            success: true,
            action: 'api_delegation_required',
            message: 'Please use the medication change API endpoint for this operation'
        };
    }
    /**
     * Simple method to get active subscriptions directly from Stripe
     * This replaces complex billing display logic with direct Stripe truth
     */
    static async getActiveSubscriptions(patientId) {
        // Client-side detection: delegate to API route when running in browser
        if (typeof window !== 'undefined') {
            try {
                const response = await fetch('/api/stripe/active-subscriptions', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({ patientId })
                });
                if (!response.ok) {
                    const errorText = await response.text();
                    throw new Error(`API Error: ${response.status} - ${errorText}`);
                }
                return await response.json();
            }
            catch (error) {
                console.error('Client-side active subscriptions API call failed:', error);
                return {
                    success: false,
                    error: error instanceof Error ? error.message : 'Failed to get active subscriptions'
                };
            }
        }
        // Server-side implementation
        try {
            const stripeInstance = getStripeInstance();
            // Get patient's Stripe customer ID
            const supabaseClient = createClient(getEnvironmentAwareSupabaseUrl(), getEnvironmentAwareSupabaseServiceKey());
            const { data: patient, error: patientError } = await supabaseClient
                .from('patients')
                .select('stripe_customer_id')
                .eq('id', patientId)
                .single();
            if (patientError || !patient?.stripe_customer_id) {
                throw new Error('Patient not found or missing Stripe customer ID');
            }
            // Get all active subscriptions with product details
            const subscriptions = await stripeInstance.subscriptions.list({
                customer: patient.stripe_customer_id,
                status: 'active',
                expand: ['data.items.data.price.product']
            });
            // Transform subscriptions into simple display format
            const activeSubscriptions = subscriptions.data.map(sub => {
                const priceItem = sub.items.data[0];
                const product = priceItem.price.product;
                const price = priceItem.price;
                // Determine subscription type based on product name
                let subscriptionType = 'unknown';
                let displayName = product.name;
                if (product.name.toLowerCase().includes('tracking') || product.name.toLowerCase().includes('platform')) {
                    subscriptionType = 'platform';
                    displayName = 'Med + Tracking Plan';
                }
                else if (product.name.toLowerCase().includes('medication') || product.name.toLowerCase().includes('tirzepatide') || product.name.toLowerCase().includes('semaglutide')) {
                    subscriptionType = 'medication';
                    displayName = product.name;
                }
                return {
                    subscriptionId: sub.id,
                    subscriptionType,
                    productId: product.id,
                    productName: product.name,
                    displayName,
                    amount: price.unit_amount ? price.unit_amount / 100 : 0,
                    currency: price.currency,
                    interval: price.recurring?.interval || 'month',
                    intervalCount: price.recurring?.interval_count || 1,
                    status: sub.status,
                    currentPeriodStart: new Date(sub.current_period_start * 1000).toISOString(),
                    currentPeriodEnd: new Date(sub.current_period_end * 1000).toISOString(),
                    nextBillingDate: new Date(sub.current_period_end * 1000).toISOString(),
                    cancelAtPeriodEnd: sub.cancel_at_period_end,
                    createdAt: new Date(sub.created * 1000).toISOString()
                };
            });
            return {
                success: true,
                data: activeSubscriptions
            };
        }
        catch (error) {
            console.error('❌ Failed to get active subscriptions:', error);
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Failed to retrieve active subscriptions'
            };
        }
    }
}
